import Button from 'src/components/buttons/button';
import ActionsDropdown from 'src/modules/community/singlePost/components/actionsDropdown';
import LikeButton from 'src/components/buttons/likeButton';
import FollowButton from 'src/components/buttons/followButton';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import { ModalType } from 'src/constants/modalTypes';
import { useDispatch, useSelector } from 'src/store/store';
import { Permission } from 'src/constants/types';
import { userHasPermission } from 'src/utils/context';
import { setAlert } from 'src/components/alert/actions';
import i18n from 'src/utils/translate';
import { ModalProps } from 'src/modules/shared/modal/components/report/reportModal';

export default function PostActions({ onReply }: { onReply: () => void }): JSX.Element {
  const dispatch = useDispatch();
  const { currentCommunity, currentUser } = useSelector(({ context }) => context);
  const { post } = useSelector(({ pages }) => pages.singlePost);

  const canReport = userHasPermission(currentUser, Permission.SendReportAbuse);
  const canFollow = currentUser &&
    post.author.id !== currentUser?.userId &&
    // private post can only be followed by community members
    (!post.isPrivate || (post.isPrivate && currentUser?.isCommunityMemberOrHUStaff)) &&
    !post.isLocked;

  const onReplyClick = () => {
    if (!!onReply && !!currentUser) {
      if (!userHasPermission(currentUser, Permission.CreatePost)) {
        dispatch(setAlert(i18n.t(`You have been restricted in this community. Please contact this community's administrator to find out why.`)));
        return;
      }
      onReply();
    } else {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: {
          section: Section.POST,
          element: Element.REPLY,
        },
      });
      dispatch(showSignupModal());
    }
  };

  const onReportClick = () => {
    if (currentUser) {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'report',
        clickMetadata: { section: Section.POST },
      });
      const modalProps: ModalProps = {
        reportType: 'post',
        postId: post.id,
        communitySlug: currentCommunity?.slug,
        footfallSection: Section.POST,
      };
      dispatch(showModal({ modalType: ModalType.Report, modalProps }));
    } else {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: {
          section: Section.POST,
          element: Element.REPORT,
        },
      });
      dispatch(showSignupModal());
    }
  };

  return (
    <div className="post-actions">
      {!post.isLocked && !currentCommunity.isArchived && (currentUser?.isCommunityMemberOrHUStaff || !currentUser) && (
        <Button
          dataTestId="reply-button"
          size="sm"
          onClick={onReplyClick}
        >
          {i18n.t('Reply')}
        </Button>
      )}
      <LikeButton
        post={post}
        signupContext={{
          section: Section.POST,
          element: Element.LIKE,
        }}
      />
      {(canFollow || !currentUser) && (
        <FollowButton
          buttonText={i18n.t('Save post')}
          dataTestId="save-button"
          followIds={[post.id]}
          followType="post"
          isFollowing={post.isFollowing}
          noIcon={true}
          signupContext={{
            section: Section.POST,
            element: Element.FOLLOW,
          }}
          size="sm"
        />
      )}
      {(!currentUser || canReport) && (
        <Button dataTestId="report-button" type="link" onClick={onReportClick}>
          {i18n.t('Report')}
        </Button>
      )}
      {currentUser && (
        <ActionsDropdown
          buttonText={i18n.t('More')}
          dataTestId="spp-more-dropdown"
          post={post}
        />
      )}
    </div>
  );
}
